<template>
  <div>
    <module-actions
      v-on:search="search($event)"
      :searchStatus="searchStatus"
      :createStatus="createStatus"
      :createBtnLabel="createBtnLabel"
      :createRouteName="createRouteName"
      :createRouteParams="createRouteParams"
      :downloadSampleStatus="downloadSampleStatus"
      :downloadSampleUrl="downloadSampleUrl"
      :uploadFileStatus="uploadFileStatus"
      :uploadFileUrl="uploadFileUrl"
    />

    <div
      class="table-header"
      v-if="Object.keys(excelFields).length && listOfData.length"
    >
      <div>
        <button
          class="btn btn-outline-primary btn-sm mx-1"
          @click="selectAllRows"
        >
          {{ $t("form.selectAll") }}
        </button>
        <button
          class="btn btn-outline-primary btn-sm mx-1"
          @click="clearSelected"
        >
          {{ $t("form.clearSelected") }}
        </button>
      </div>
      <div class="d-flex">
        <DownloadExcel
          v-if="selected.length"
          className="btn btn-outline-primary btn-sm"
          :fields="excelFields"
          :data="selected"
          :name="excelName"
          class="mx-2"
        />
        <slot name="filter" />
      </div>
    </div>
    <b-table
      id="table-transition-example"
      class="vgt-table bordered mb-1"
      :items="listOfData"
      name="id"
      :fields="fields"
      responsive
      striped
      show-empty
      :tbody-transition-props="transProps"
      primary-key="id"
      :busy="loadingTable"
      @sort-changed="sortChanged"
      :no-sort-reset="true"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner type="grow" label="Loading..." variant="primary" />
        </div>
      </template>

      <template v-slot:[`cell(${$t(`global.selected`)})`]="data">
        <b-form-checkbox
          v-model="selected"
          :value="data.item"
        ></b-form-checkbox>
      </template>

      <template #cell(#)="data">
        {{ pagination.startItemInPage + data.index }}
      </template>

      <template
        v-for="(field, key) in fields"
        v-slot:[`cell(${field.key})`]="data"
      >
        <div :key="key">
          <!-- handle Image -->
          <b-avatar
            v-if="field.type == 'image'"
            class="avatar"
            :src="
              $_.get(data.item, field.key)
                ? 'https://jockey2.frusiya.dafa.dev:21000' +
                  $_.get(data.item, field.key)
                : ''
            "
          />

          <!-- handle status -->
          <strong v-else-if="field.type == 'status'">
            <mainstatus :status="$_.get(data.item, field.key)" />
          </strong>

          <!-- actions -->
          <div v-else-if="field.type == 'actions'">
            <cell-actions
              :cell-actions="field"
              :data-item="data.item"
              :baseURL="baseURL"
            />
          </div>

          <!-- Array handler -->
          <div v-else-if="field.type == 'array'">
            <span v-if="field.array_keys">
              <ul class="p-0">
                <li
                  v-for="(arr, key) in $_.get(data.item, field.key)"
                  :key="key"
                >
                  <span v-for="(ind, key1) in field.array_keys" :key="key1"
                    >{{ arr[ind] }}
                  </span>
                </li>
              </ul>
            </span>
            <span v-else>
              <span
                v-for="(arr, key) in $_.get(data.item, field.key)"
                :key="key"
              >
                {{ arr }}
                <span v-if="key < $_.get(data.item, field.key).length - 1"
                  >,</span
                >
              </span>
            </span>
          </div>

          <!-- Multi-image handler -->
          <div
            class="min-width-image-cell"
            v-else-if="field.type == 'multi_image'"
          >
            <div class="iq-media-group position-relative">
              <b-link
                href="#"
                class="iq-media"
                v-for="(image, counter) in $_.get(data.item, field.key).slice(
                  0,
                  3
                )"
                :key="counter"
              >
                <b-img
                  class="avatar-50"
                  rounded="circle"
                  fluid
                  :src="image.image"
                  :alt="image.name"
                />
                <div
                  v-if="
                    $_.get(data.item, field.key).length > 3 && counter === 2
                  "
                  class="more-images text-white"
                >
                  {{ $_.get(data.item, field.key).length - 3 }}+
                </div>
              </b-link>
            </div>
          </div>

          <!-- Multi-text handler -->
          <div v-else-if="field.type == 'multi-text'">
            <span v-for="(arrKey, key) in field.key.split(',')" :key="key">
              <span v-if="$_.get(data.item, arrKey)">
                {{ $_.get(data.item, arrKey) }}
              </span>
            </span>
          </div>

          <!-- Multi-value handler -->
          <div v-else-if="field.type == 'multi-value'">
            <ul class="p-0">
              <li v-for="(arrKey, key) in field.key.split(',')" :key="key">
                <span v-if="$_.get(data.item, arrKey) > 0">
                  <span v-if="arrKey.includes('egp')">EGP </span>
                  <span v-else-if="arrKey.includes('euro')">EUR </span>
                  <span v-else-if="arrKey.includes('dollar')">$ </span>
                  {{ $_.get(data.item, arrKey) }}
                </span>
                <span v-else>N/A</span>
              </li>
            </ul>
          </div>

          <!-- handle dateTime -->
          <strong v-else-if="field.type == 'dateTime'">
            <span>{{ $_.get(data.item, field.key) | formatDateTime }}</span>
          </strong>
          <strong v-else-if="field.type == 'date'">
            <span>{{ $_.get(data.item, field.key) | formatDate }}</span>
          </strong>
          <strong v-else-if="field.type == 'time'">
            <span>{{ $_.get(data.item, field.key) | formatTime }}</span>
          </strong>

          <!-- handle boolean -->
          <strong v-else-if="field.type == 'boolean'">
            <span>{{
              $_.get(data.item, field.key) ? $t("global.yes") : $t("global.no")
            }}</span>
          </strong>

          <!-- handle Text -->
          <p v-else class="text-nowrap m-0">
            <span v-if="$_.get(data.item, field.key)">
              {{
                $_.get(data.item, field.key).length > 30
                  ? $_.get(data.item, field.key).substring(0, 30) + "..."
                  : $_.get(data.item, field.key)
              }}
            </span>
            <span v-else> --- </span>
          </p>
        </div>
      </template>
      <template v-slot:cell(change_status)="data">
        <changeStatus
          :allData="data"
          :id="data.item.id"
          :type="data.field.tableType"
          :status="data.item.status"
          :statusKeyId="data.field.idKey"
          @changeStatus="changeStatus"
        />
      </template>
    </b-table>
    <!-- <div class="bg-primary py-2 px-3 d-flex justify-content-between align-items-center iq-border-radius-5 mt-0">
      <span>8-1 من 24 مستخدم</span>
      <div class="d-flex flex-nowrap justify-content-end align-items-center gap_1 flex-grow-1">
          <span>الصفوف لكل صفحة</span>
          <b-select class="footer-select">
            <b-select-option value="1">1</b-select-option>
            <b-select-option value="2">2</b-select-option>
            <b-select-option value="3">3</b-select-option>
          </b-select>
          <div class="d-flex">
          <span class="bg-white iq-border-radius-5 arroy-footer-table mb-0 ml-1">
              <i class="las la-arrow-right text-primary"></i>
          </span>
            <span class="bg-white iq-border-radius-5 arroy-footer-table mb-0">
              <i class="las la-arrow-left text-primary"></i>
          </span>
          </div>
        </div>
    </div> -->
    <b-pagination
      v-if="pagination.total_pages > 1"
      v-model="pagination.current_page"
      :total-rows="pagination.total"
      :per-page="10"
      first-number
      last-number
      class="mb-3 mt-2"
      prev-class="prev-item"
      next-class="next-item"
      align="center"
      @input="getListData"
    >
    </b-pagination>
  </div>
</template>

<script>
import Bus from "@/eventBus";
import mainstatus from "./status";
import mainService from "@/services/main";
import cellActions from "./cellActions";
import changeStatus from "./changeStatus";
import { core } from "@/config/pluginInit";
import DownloadExcel from "@/components/global/DownloadExcel.vue";
import moment from "moment";

export default {
  components: {
    mainstatus,
    cellActions,
    changeStatus,
    DownloadExcel,
  },
  props: {
    fields: { type: Array, required: true },
    items: { type: [Array, Object], required: false },
    list_url: { type: String },
    additionalUrl: { type: String },
    params: { type: [Object, Array] },
    reloadData: { type: Boolean, default: false },
    baseURL: { type: String, default: "" },
    excelFields: { type: Array, default: () => [] },
    excelName: { type: String, default: "" },
    createStatus: { type: Boolean, default: true },
    createBtnLabel: { type: String, default: "" },
    createRouteName: { type: String, default: "" },
    createRouteParams: { type: Object, default: () => {} },
    downloadSampleStatus: { default: false, type: Boolean },
    downloadSampleUrl: { default: "", type: String },
    uploadFileStatus: { default: false, type: Boolean },
    uploadFileUrl: { default: "", type: String },
  },
  data() {
    return {
      transProps: {
        name: "flip-list",
      },
      listOfData: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
      },
      loadingTable: false,
      moreImages: 2,
      selected: [],
    };
  },
  watch: {
    params(a) {
      this.getListData();
    },
    reloadData(val) {
      if (val) {
        // this.listOfData = []
        this.getListData();
      }
    },
  },
  created() {
    this.getListData();
  },
  computed: {
    searchStatus() {
      // eslint-disable-next-line no-prototype-builtins
      return this.params?.hasOwnProperty("search") || false;
    },
  },
  methods: {
    async getListData() {
      this.listOfData = [];
      let filters = {};
      if (this.params) {
        filters = this.params;
      }
      this.loadingTable = true;
      let List = [];
      if (!Array.isArray(this.items) || !this.items?.length > 0) {
        List = await mainService.listDataTabl(
          `${this.list_url}?page=${
            this.pagination.current_page
          }&limit=10&name=${filters?.search || ""}${
            this.additionalUrl ? this.additionalUrl : ""
          }`,
          filters
        );
        if (Array.isArray(List.data.data)) {
          // this.items = List.data.data
          this.listOfData = List.data.data;
          this.pagination = {
            current_page: List.data?.meta?.current_page,
            per_page: List.data?.meta?.per_page,
            total: List.data?.meta?.total,
            total_pages: List.data?.meta?.total_pages,
          };
        } else {
          this.listOfData = [List.data.data];
        }
      } else {
        this.listOfData = [List.data.data];
      }
      this.loadingTable = false;
    },
    sortChanged(data) {
      this.$emit("sortChanged", data);
    },
    changeStatus(data) {
      mainService.changeStatus(data.payload).then((res) => {
        core.showSnackbar("success", res.data.message);
        const IndexRow = this.listOfData.findIndex(
          (row) => row.id === data.data.item.id
        );
        if (this.listOfData[IndexRow].status === "active") {
          this.listOfData[IndexRow].status = "inactive";
          this.listOfData[IndexRow]._rowVariant = "secondary";
        } else {
          this.listOfData[IndexRow].status = "active";
          this.listOfData[IndexRow]._rowVariant = "";
        }
      });
    },
    selectAllRows() {
      this.selected = this.listOfData;
    },
    clearSelected() {
      this.selected = [];
    },
    search(text) {
      this.params.search = text.trim();
      this.reloadData = true;
      setTimeout(() => {
        this.reloadData = false;
      }, 500);
    },
    moment,
  },
  mounted() {
    Bus.$on("reloadTableAfterDelete", (ifReload) => {
      this.reloadData = true;
    });
  },
};
</script>
<style>
table#table-transition-example .flip-list-move {
  transition: transform 0.5s;
}
.footer-select {
  width: 70px !important;
}
.arroy-footer-table {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  width: 40px;
  height: calc(1.5em + 0.75rem + 2px);
  font-size: 20px;
}
.arroy-footer-table i {
  font-size: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  background: #f6ecff;
  padding: 5px 8px;
  border-radius: 5px;
}
</style>
